import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Archive, FileCode2, FileText, Printer } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import {
  colorDelEstado,
  colorDeLaSituacion,
  type FacturaListada,
} from '@/modules/facturacion/estadosDeFactura';
import { useComprobante } from '@/modules/facturacion/useComprobante';

export function FacturasDelCliente({ identificacion }: { identificacion: string }) {
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(10);
  const comprobante = useComprobante();

  const facturas = useQuery({
    queryKey: ['facturas-del-cliente', identificacion, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<FacturaListada>>(
        `/facturas?buscar=${encodeURIComponent(identificacion)}&pagina=${pagina}&tamanio=${tamanio}`,
      ),
    enabled: identificacion.length > 0,
  });

  const columnas: Columna<FacturaListada>[] = [
    {
      clave: 'periodo',
      encabezado: 'Período',
      contenido: (f) => (
        <span className="flex items-center gap-2">
          {f.periodo}
          {f.historica && (
            <span
              className="inline-flex items-center gap-1 rounded bg-armazon-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-armazon-500"
              title="Emitida por el sistema anterior"
            >
              <Archive className="size-3" aria-hidden />
              Histórica
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'numero',
      encabezado: 'Nº factura',
      contenido: (f) => (
        <span className="font-mono text-xs">
          {f.numero ?? <span className="text-armazon-400">SIN EMITIR</span>}
        </span>
      ),
    },
    {
      clave: 'vencimiento',
      encabezado: 'Vence',
      contenido: (f) => (
        <span className="whitespace-nowrap text-[11px] text-armazon-500">
          {enFecha(f.vencimiento)}
        </span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (f) => <span className="importe">${f.total.toFixed(2)}</span>,
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      alinear: 'centro',
      contenido: (f) => (
        <span className={`text-[11px] font-semibold uppercase ${colorDelEstado(f.estado)}`}>
          {f.estado}
        </span>
      ),
    },
    {
      clave: 'situacionSri',
      encabezado: 'Ante el SRI',
      alinear: 'centro',
      contenido: (f) => (
        <span
          className={`text-[11px] font-semibold uppercase ${colorDeLaSituacion(f.situacionSri)}`}
        >
          {f.historica ? '—' : f.situacionSri}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      contenido: (f) =>
        f.situacionSri === 'autorizada' ? (
          <span className="flex justify-end gap-1">
            <BotonDeFila
              icono={Printer}
              titulo="Abrir el RIDE"
              alPulsar={() => void comprobante.abrirRide(f.id, f.historica)}
              ocupado={comprobante.ocupado === f.id}
            />
            <BotonDeFila
              icono={FileCode2}
              titulo="Descargar el XML autorizado"
              alPulsar={() => void comprobante.descargarXml(f.id, f.numero, f.historica)}
              ocupado={comprobante.ocupado === f.id}
            />
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <Tabla
        datos={facturas.data}
        cargando={facturas.isLoading}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(nuevo) => {
          setTamanio(nuevo);
          setPagina(0);
        }}
        iconoVacio={FileText}
        mensajeVacio="Este cliente no tiene facturas"
        entidad="FACTURAS"
      />

      <VistaPreviaDeImpresion
        abierto={comprobante.ride !== null || comprobante.ocupado !== null}
        alCerrar={comprobante.cerrar}
        titulo="RIDE"
        subtitulo="Representación impresa del comprobante electrónico"
        fichero={comprobante.ride}
        cargando={comprobante.ocupado !== null}
        formato="hoja"
        textoDeCarga="Preparando el RIDE…"
      />
    </>
  );
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return dia && mes && anio ? `${dia}/${mes}/${anio}` : iso;
}
